/**
 * AES-256 block encryption, for the one thing WebCrypto cannot do here.
 *
 * Zip's AES format (WinZip AE-2) counts its CTR blocks little-endian, while
 * `crypto.subtle`'s AES-CTR counts big-endian — and there is no arithmetic that
 * turns one sequence into the other, so the keystream has to be built a block
 * at a time. One WebCrypto call per 16 bytes would be thousands of promises for
 * a database backup; this does the same work in one pass.
 *
 * Everything else — the key derivation and the authentication — stays with
 * WebCrypto, where it belongs. `test/run.mjs` checks this against WebCrypto's
 * own AES on random keys and blocks.
 */
const SBOX = new Uint8Array(256)

const rotl8 = (x, shift) => ((x << shift) | (x >>> (8 - shift))) & 0xff

// The S-box is derived rather than pasted in: 256 magic bytes in a source file
// are impossible to check by eye, and the maths is five lines.
;(() => {
  let p = 1
  let q = 1
  do {
    p = (p ^ ((p << 1) & 0xff) ^ (p & 0x80 ? 0x1b : 0)) & 0xff
    q ^= (q << 1) & 0xff
    q ^= (q << 2) & 0xff
    q ^= (q << 4) & 0xff
    if (q & 0x80) q ^= 0x09
    q &= 0xff
    SBOX[p] = (q ^ rotl8(q, 1) ^ rotl8(q, 2) ^ rotl8(q, 3) ^ rotl8(q, 4) ^ 0x63) & 0xff
  } while (p !== 1)
  SBOX[0] = 0x63
})()

const xtime = (b) => ((b << 1) ^ (b & 0x80 ? 0x1b : 0)) & 0xff

/** The 15 round keys of an AES-256 schedule, as one flat array of bytes. */
export function expandKey(key) {
  if (key.length !== 32) throw new Error('AES-256 needs a 32-byte key')
  const rounds = 14
  const words = 4 * (rounds + 1)
  const out = new Uint8Array(words * 4)
  out.set(key)
  let rcon = 1
  for (let i = 8; i < words; i++) {
    const prev = (i - 1) * 4
    let a = out[prev]
    let b = out[prev + 1]
    let c = out[prev + 2]
    let d = out[prev + 3]
    if (i % 8 === 0) {
      ;[a, b, c, d] = [SBOX[b] ^ rcon, SBOX[c], SBOX[d], SBOX[a]]
      rcon = xtime(rcon)
    } else if (i % 8 === 4) {
      ;[a, b, c, d] = [SBOX[a], SBOX[b], SBOX[c], SBOX[d]]
    }
    const back = (i - 8) * 4
    const at = i * 4
    out[at] = out[back] ^ a
    out[at + 1] = out[back + 1] ^ b
    out[at + 2] = out[back + 2] ^ c
    out[at + 3] = out[back + 3] ^ d
  }
  return out
}

/** Encrypts one 16-byte block in place-ish, writing into `out`. */
export function encryptBlock(schedule, block, out = new Uint8Array(16)) {
  const state = out
  for (let i = 0; i < 16; i++) state[i] = block[i] ^ schedule[i]

  for (let round = 1; round <= 14; round++) {
    for (let i = 0; i < 16; i++) state[i] = SBOX[state[i]]

    // ShiftRows: row r moves left by r, the state being column-major.
    let t = state[1]
    state[1] = state[5]
    state[5] = state[9]
    state[9] = state[13]
    state[13] = t
    t = state[2]
    const u = state[6]
    state[2] = state[10]
    state[6] = state[14]
    state[10] = t
    state[14] = u
    t = state[15]
    state[15] = state[11]
    state[11] = state[7]
    state[7] = state[3]
    state[3] = t

    if (round < 14) {
      for (let c = 0; c < 16; c += 4) {
        const a0 = state[c]
        const a1 = state[c + 1]
        const a2 = state[c + 2]
        const a3 = state[c + 3]
        const all = a0 ^ a1 ^ a2 ^ a3
        state[c] ^= all ^ xtime(a0 ^ a1)
        state[c + 1] ^= all ^ xtime(a1 ^ a2)
        state[c + 2] ^= all ^ xtime(a2 ^ a3)
        state[c + 3] ^= all ^ xtime(a3 ^ a0)
      }
    }

    const round_key = round * 16
    for (let i = 0; i < 16; i++) state[i] ^= schedule[round_key + i]
  }
  return state
}

/**
 * XORs `data` with AES-CTR keystream, counting blocks little-endian from 1 —
 * which is what zip's AES format asks for. Encryption and decryption are the
 * same operation.
 */
export function ctrLittleEndian(key, data) {
  const schedule = expandKey(key)
  const counter = new Uint8Array(16)
  const keystream = new Uint8Array(16)
  const out = new Uint8Array(data.length)
  let block = 0n

  for (let at = 0; at < data.length; at += 16) {
    block += 1n
    let value = block
    for (let i = 0; i < 16; i++) {
      counter[i] = Number(value & 0xffn)
      value >>= 8n
    }
    encryptBlock(schedule, counter, keystream)
    const end = Math.min(at + 16, data.length)
    for (let i = at; i < end; i++) out[i] = data[i] ^ keystream[i - at]
  }
  return out
}
